import type { Point } from "@/core/types";

/**
 * PR32 (Snake Movement Smoothing) + PR32 follow-ups. `LevelGame.tick()`'s
 * logical snake position is, and remains, an instantaneous integer-cell
 * jump every tick - see the PR32 architecture audit. This module is the
 * ENTIRE render-only motion layer on top of that: it never reads or
 * mutates any `LevelGame` state, and nothing it computes is ever fed back
 * into gameplay (collision, fruit pickup, portals, WORMHOLE, scoring,
 * input).
 *
 * PR32 follow-up (route/trail replacement). Earlier revisions of this
 * module interpolated each body segment independently
 * (`previous[i] -> current[i]`, all driven by the same global progress).
 * On a long snake this meant dozens of segments each re-doing their own
 * fresh short slide EVERY tick, all in lockstep - visually "many pieces
 * moving/morphing at once" rather than one body moving through its own
 * history. This revision replaces that with a genuine ROUTE: `GameScene`
 * retains a short, bounded history of the HEAD's own committed path
 * (`RoutePoint[]`, oldest to newest), and the entire visible body -
 * including the head - is produced by walking BACKWARD along that route
 * from the head's own fractional visual position, sampling one point per
 * grid cell of arc-length (`sampleSnakeRoute()`). A turn's corner is then
 * a genuine, fixed point in that history: as the head keeps moving, the
 * corner's position relative to the CURRENT sample points shifts
 * smoothly backward through the body, one cell at a time - it never
 * "morphs" simultaneously across every segment on the same tick. Growth/
 * SHRINK only change how many samples are drawn from the SAME route, not
 * how the route itself is built.
 *
 * PR32 follow-up (continuous-body art pass): also owns the pure
 * continuous-body-path geometry (`computeSnakeVisualRuns`,
 * `isVisuallyContinuous`, `computeBodyEdgeQuad`) `GameScene`/
 * `VisualFeedbackController` use to render the snake as one continuous
 * shape instead of a row of separately shaped cells, splitting cleanly
 * at any genuine portal/WORMHOLE discontinuity. `sampleSnakeRoute()`'s
 * OUTPUT is still just a plain `Point[]` (fractional grid coordinates),
 * so this geometry keeps working completely unchanged on top of it - a
 * fallback-to-logical-position sample (see `sampleSnakeRoute()`'s own
 * doc) is exactly the kind of spatial discontinuity this geometry was
 * already designed to split into a separate run.
 *
 * Deliberately Phaser-independent and directly unit-testable, the same
 * pattern `src/core/SpeedEngine.ts`/`src/core/MovementAccumulator.ts`
 * already use for logic `GameScene` itself has no test harness for.
 */

// ---------------------------------------------------------------------
// Motion curve / timing
// ---------------------------------------------------------------------

/**
 * The fraction of the CURRENT movement interval the visual transition
 * takes to complete. The single centralized tunable for this feature -
 * safe to retune after Deploy Preview playtesting without touching any
 * call site (every caller always goes through
 * `computeSnakeTransitionDurationMs()`, never this constant directly).
 * `1` (a full-step transition, captured once per tick, never a
 * live-changing denominator) - unchanged by the route/trail replacement;
 * see `easeFrontLoaded()` for the current curve shape.
 */
export const SNAKE_RENDER_SMOOTHING_FRACTION = 1;

/**
 * `stepMs * SNAKE_RENDER_SMOOTHING_FRACTION`, floored at `0` for a
 * pathological non-positive `stepMs` (never expected from a real
 * `LevelGameSnapshot.stepMs`) - `computeSnakeRenderProgress()` treats a
 * `0` duration as an immediate snap, never a divide-by-zero.
 */
export function computeSnakeTransitionDurationMs(stepMs: number): number {
  return Math.max(0, stepMs) * SNAKE_RENDER_SMOOTHING_FRACTION;
}

/**
 * PR32 follow-up (motion-curve replacement). `f(t) = t + a * t * (1 - t)`,
 * `a = SNAKE_MOTION_FRONT_LOAD_FACTOR`. Replaces `easeOutQuad`
 * (`1 - (1-t)^2`), whose derivative `2(1-t)` reaches exactly `0` at
 * `t=1` - every grid step visually decelerated to a dead stop right at
 * the cell boundary, then the next step restarted at full velocity
 * (derivative `2` at `t=0`), producing a repeated per-cell pulse. This
 * family keeps a positive velocity at BOTH ends: `f'(0) = 1+a`,
 * `f'(1) = 1-a` - for `0 < a < 1`, `1-a` is always strictly positive, so
 * motion never visually stalls right before the next tick takes over.
 * `f(0)=0`, `f(1)=1` exactly (no overshoot: `f` is strictly increasing
 * on `[0,1]` since `f'(t) = 1 + a*(1-2t)` is a decreasing linear function
 * that is already at its minimum, `1-a`, at `t=1`, and `1-a > 0` for our
 * chosen `a` - so `f'(t) > 0` for every `t` in `[0,1]`, meaning `f`
 * itself never exceeds `1` or dips below `0` in between). `t` is clamped
 * to `[0, 1]` first, so an out-of-range input can never produce a value
 * outside `[0, 1]` either.
 */
export const SNAKE_MOTION_FRONT_LOAD_FACTOR = 0.3;

export function easeFrontLoaded(t: number): number {
  const clamped = Math.max(0, Math.min(1, t));
  return clamped + SNAKE_MOTION_FRONT_LOAD_FACTOR * clamped * (1 - clamped);
}

/**
 * `0` at the instant a transition begins, `1` once `elapsedMs` reaches
 * `durationMs` - always clamped to `[0, 1]`, never `NaN`. Both inputs are
 * render-only, ACTIVE-time values the caller owns and advances itself
 * (this function performs no clock read of its own) - so this is exactly
 * as pause-safe as the caller's own `elapsedMs` accumulation: freezing
 * whenever the caller simply stops advancing it, exactly like every
 * other active-time value in this codebase (`SpeedEngine`, WORMHOLE/
 * SPEED TRIAL timers, ...). `elapsedMs` is expected to only ever
 * increase and `durationMs` to be fixed for the lifetime of one
 * transition (see `computeSnakeTransitionDurationMs()`'s call site,
 * captured ONCE when a transition starts, never re-read against a live,
 * possibly-since-changed `stepMs`) - under that contract, the underlying
 * linear `t = elapsedMs / durationMs` is monotonic by construction and
 * can never move backward, even if the movement interval itself changes
 * mid-transition (a Speed Engine retarget only ever affects the NEXT
 * transition's duration).
 *
 * The returned value is `easeFrontLoaded(t)`, not raw linear `t` - see
 * that function's own doc. Applying a monotonically increasing function
 * to an already-monotonic, already-clamped `[0, 1]` input preserves both
 * properties exactly, so every existing guarantee this function's
 * callers already depend on (never negative, never above `1`, never
 * `NaN`, freezes under pause, a fresh transition always restarts cleanly
 * at `0`) holds completely unchanged - only the shape of the curve
 * BETWEEN `0` and `1` changed.
 */
export function computeSnakeRenderProgress(elapsedMs: number, durationMs: number): number {
  if (durationMs <= 0) return 1;
  if (elapsedMs <= 0) return 0;
  const linear = Math.min(1, elapsedMs / durationMs);
  return easeFrontLoaded(linear);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpPoint(a: Point, b: Point, t: number): Point {
  return { x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t) };
}

// ---------------------------------------------------------------------
// Continuous-body run/edge geometry (unchanged by the route/trail
// replacement - still the ONLY thing that decides "is this pair of
// rendered points close enough to draw as one continuous piece", now
// applied to `sampleSnakeRoute()`'s output instead of the old per-index
// lerp's output).
// ---------------------------------------------------------------------

/**
 * How far apart (in grid cells) two consecutive rendered points may be
 * and still be considered "the same physical joint" - part of the same
 * continuous visual run. Ordinary movement (straight OR a single
 * 90-degree turn) keeps two ADJACENT logical cells within `[sqrt(2)/2, 1]`
 * of each other throughout any transition; `1.05` leaves a small margin
 * for floating-point slack while staying far below any GENUINE
 * discontinuity (a portal/WORMHOLE-teleported head, always several cells
 * away at minimum, or a route/logical-position fallback boundary - see
 * `sampleSnakeRoute()`) - so run membership can always be decided from
 * geometry alone, with no portal/WORMHOLE/route-state flag ever
 * consulted here.
 */
const VISUAL_CONTINUITY_MAX_GRID_DISTANCE = 1.05;

/**
 * Whether two consecutive rendered points belong in the same continuous
 * visual run/route edge, in GRID-space (cell units, not pixels) - see
 * `VISUAL_CONTINUITY_MAX_GRID_DISTANCE`'s own doc for the exact bound.
 * The SAME point (distance `0`) counts as continuous too - it is
 * definitionally not a discontinuity.
 */
export function isVisuallyContinuous(a: Point, b: Point): boolean {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const distanceSquared = dx * dx + dy * dy;
  return distanceSquared <= VISUAL_CONTINUITY_MAX_GRID_DISTANCE * VISUAL_CONTINUITY_MAX_GRID_DISTANCE;
}

/**
 * Splits `snake` (head-first, render-only positions - the array
 * `sampleSnakeRoute()` returns) into maximal runs of consecutive points
 * that are all mutually connected by `isVisuallyContinuous()` - i.e. one
 * array per continuous visual body piece. Every real, ordinary snake
 * produces exactly one run spanning the whole body; a portal or WORMHOLE
 * teleport, or a route-history fallback boundary (see
 * `sampleSnakeRoute()`), always produces two or more runs - and NEVER a
 * spurious merge between two runs that are not actually adjacent in body
 * order (only consecutive INDICES are ever compared, one pair at a
 * time). `[]` only for an empty `snake`; every non-empty `snake`
 * produces at least one run, and every input point appears in exactly
 * one run, in its original order - nothing is ever dropped or
 * duplicated. Pure and deterministic: the same `snake` array always
 * splits the same way.
 */
export function computeSnakeVisualRuns(snake: readonly Point[]): Point[][] {
  if (snake.length === 0) return [];
  const runs: Point[][] = [];
  let current: Point[] = [snake[0]];
  for (let i = 1; i < snake.length; i++) {
    if (isVisuallyContinuous(snake[i - 1], snake[i])) {
      current.push(snake[i]);
    } else {
      runs.push(current);
      current = [snake[i]];
    }
  }
  runs.push(current);
  return runs;
}

/**
 * The 4 corner points of a straight-edged quad bridging `centerA` to
 * `centerB` (same units as its inputs - `GameScene` passes PIXEL-space
 * segment centers and a pixel `thickness`), oriented along the A->B
 * direction and centered on that line, `thickness` wide. `[]` for two
 * coincident centers (nothing to draw), so this can never divide by
 * zero. Intended to be filled UNDER each run's own end-cap circles
 * (drawn first, then circles drawn on top) - deliberately straight-edged,
 * not rounded: the overlapping circles at both ends are what give the
 * run's silhouette its rounded caps/joins, so this edge quad never needs
 * to reproduce that separately.
 */
export function computeBodyEdgeQuad(centerA: Point, centerB: Point, thickness: number): Point[] {
  const dx = centerB.x - centerA.x;
  const dy = centerB.y - centerA.y;
  const length = Math.hypot(dx, dy);
  if (length === 0) return [];
  const halfThickness = thickness / 2;
  const nx = (-dy / length) * halfThickness;
  const ny = (dx / length) * halfThickness;
  return [
    { x: centerA.x + nx, y: centerA.y + ny },
    { x: centerB.x + nx, y: centerB.y + ny },
    { x: centerB.x - nx, y: centerB.y - ny },
    { x: centerA.x - nx, y: centerA.y - ny },
  ];
}

// ---------------------------------------------------------------------
// PR32 final polish: render-only turn softening and tail taper. Both
// operate on the SAME grid-space run arrays `computeSnakeVisualRuns()`
// produces (BEFORE `GameScene` converts each point to pixel space) - pure
// geometric transforms of the already-sampled centerline, never touching
// `sampleSnakeRoute()`'s own output or the route/trail model it samples
// from. Applied per-run (never across a run boundary), so a genuine
// portal/WORMHOLE discontinuity is completely unaffected by either.
// ---------------------------------------------------------------------

/**
 * How far back along each adjacent edge (as a fraction of that edge's
 * own length) a corner's chamfer points are pulled - see
 * `softenRunCorners()`'s own doc. `0.22` keeps the softened region
 * modest (comfortably under a quarter of one grid cell on either side of
 * the corner), so two corners only 1 cell apart (the tightest a legal
 * zig-zag route can produce) can never have their own softened regions
 * overlap (`0.22 + 0.22 = 0.44 < 1`), and a long snake's bends stay
 * visually distinct rather than blurring into one soft blob.
 */
const CORNER_SOFTEN_FRACTION = 0.22;

/** How many interior points to sample along a corner's quadratic-Bezier chamfer arc - enough for a visibly smooth curve with the existing circle+quad capsule-chain primitive, without adding meaningfully to per-frame draw cost (O(1) extra points per corner, never per straight segment). */
const CORNER_ARC_SAMPLES = 3;

/**
 * How close to exactly perpendicular (dot product of the two unit edge
 * vectors, `0` for a true 90-degree turn, `+-1` for a straight
 * continuation/reversal) a point's incoming/outgoing edges must be
 * before it's treated as a real grid turn worth softening. Orthogonal
 * grid movement only ever produces an edge pair that's either exactly
 * straight or exactly perpendicular - there is no genuine in-between
 * case to misclassify - so this generous band exists only to absorb
 * ordinary floating-point sampling noise, never to catch a "sort of
 * diagonal" reading.
 */
const CORNER_DOT_THRESHOLD = 0.35;

function quadraticBezierPoint(p1: Point, control: Point, p2: Point, u: number): Point {
  const oneMinusU = 1 - u;
  const a = oneMinusU * oneMinusU;
  const b = 2 * oneMinusU * u;
  const c = u * u;
  return {
    x: a * p1.x + b * control.x + c * p2.x,
    y: a * p1.y + b * control.y + c * p2.y,
  };
}

/**
 * PR32 final polish (turn visual refinement): softens every genuine
 * 90-degree interior corner in ONE continuous run of render-only
 * centerline points into a short quadratic-Bezier chamfer arc, purely
 * for drawing. For each interior point B (never the run's first or last
 * point - a run boundary is either the head, the tail, or a genuine
 * discontinuity, and none of those are ever softened) whose incoming
 * edge A->B and outgoing edge B->C are close to perpendicular (see
 * `CORNER_DOT_THRESHOLD`), B is replaced by a short arc: two points
 * pulled back `CORNER_SOFTEN_FRACTION` of each edge's own length toward
 * A and C respectively (`p1`/`p2`), joined by a quadratic Bezier whose
 * own control point is B itself.
 *
 * Why this can never misrepresent the legal path/collision geometry: a
 * quadratic Bezier always stays within the convex hull of its 3 control
 * points. `p1` and `p2` already lie ON the original straight edges A->B
 * and B->C, and the control point is the original corner vertex B
 * itself - so the triangle {p1, B, p2} is entirely contained within the
 * region the original two straight edges already swept near the corner.
 * The arc can therefore only ever trace STRICTLY INSIDE that region -
 * it can never bulge outward past the true path, never enlarge the
 * body's visual footprint beyond what the sharp corner already covered,
 * and never implies occupying a cell the snake doesn't actually occupy.
 * It only ever shaves the extreme corner point itself.
 *
 * A straight-through point (edges nearly colinear) is left completely
 * untouched - an already-round capsule joint along a straight run needs
 * no further softening, and only a genuine turn should visually "give"
 * at the bend, so consecutive turns close together each still read as
 * their own distinct bend rather than one blurred curve.
 */
export function softenRunCorners(run: readonly Point[]): Point[] {
  if (run.length < 3) return run.slice();

  const result: Point[] = [run[0]!];
  for (let i = 1; i < run.length - 1; i++) {
    const a = run[i - 1]!;
    const b = run[i]!;
    const c = run[i + 1]!;
    const inX = b.x - a.x;
    const inY = b.y - a.y;
    const outX = c.x - b.x;
    const outY = c.y - b.y;
    const inLength = Math.hypot(inX, inY);
    const outLength = Math.hypot(outX, outY);
    if (inLength === 0 || outLength === 0) {
      result.push(b);
      continue;
    }
    const dot = (inX * outX + inY * outY) / (inLength * outLength);
    if (Math.abs(dot) > CORNER_DOT_THRESHOLD) {
      result.push(b); // not a real 90-degree turn - leave untouched
      continue;
    }
    const p1: Point = { x: b.x - inX * CORNER_SOFTEN_FRACTION, y: b.y - inY * CORNER_SOFTEN_FRACTION };
    const p2: Point = { x: b.x + outX * CORNER_SOFTEN_FRACTION, y: b.y + outY * CORNER_SOFTEN_FRACTION };
    result.push(p1);
    for (let s = 1; s <= CORNER_ARC_SAMPLES; s++) {
      result.push(quadraticBezierPoint(p1, b, p2, s / (CORNER_ARC_SAMPLES + 1)));
    }
    result.push(p2);
  }
  result.push(run[run.length - 1]!);
  return result;
}

/**
 * PR32 final polish (clearer tail): how much cumulative arc-length
 * (grid-cell units, matching this module's other route/sampling
 * constants) from the true tail endpoint the taper spans - "the last
 * ~1-2 cell lengths gradually narrow", per the design brief.
 */
export const TAIL_TAPER_LENGTH_CELLS = 1.5;

/**
 * PR32 final polish (clearer tail) + PR32 tail-taper tuning pass: the
 * radius multiplier at the exact tail tip. `0.55` (this feature's first
 * pass) read as visually too thin/dramatic on Deploy Preview - `0.7`
 * keeps the tail clearly, substantially tapered (still a full 30%
 * narrower than the body) while staying close enough to full width that
 * it reads as consistent with the snake's own collision footprint rather
 * than needle-like.
 */
export const TAIL_MIN_RADIUS_SCALE = 0.7;

/**
 * PR32 final polish (clearer tail): a per-point radius multiplier
 * (`1` = full body width) for ONE run, tapering linearly from
 * `TAIL_MIN_RADIUS_SCALE` at the run's own LAST point down to `1` at
 * `TAIL_TAPER_LENGTH_CELLS` of cumulative arc-length back from it, then
 * staying `1` for every point further back. Intended to be called ONLY
 * on the run that contains the snake's true tail (`computeSnakeVisualRuns()`
 * always places it last, as the last point of its last run - see that
 * function's own doc) - every other run keeps a uniform scale of `1`
 * (no taper), so a run boundary created by a genuine portal/WORMHOLE
 * discontinuity is never mistaken for the tail and never gets a
 * fabricated end-cap taper of its own.
 *
 * Purely a function of the run's own already-sampled points - re-derived
 * fresh every call, so growth (a longer run, taper naturally lands
 * farther back) and SHRINK (a shorter run, taper naturally lands at the
 * new, closer tail) both fall out for free with no separate animation,
 * fade, or ghost geometry.
 */
export function computeTailTaperScales(run: readonly Point[]): number[] {
  const n = run.length;
  const scales = new Array<number>(n).fill(1);
  if (n === 0) return scales;
  scales[n - 1] = TAIL_MIN_RADIUS_SCALE;
  let cumulative = 0;
  for (let i = n - 2; i >= 0; i--) {
    const a = run[i]!;
    const b = run[i + 1]!;
    cumulative += Math.hypot(a.x - b.x, a.y - b.y);
    if (cumulative >= TAIL_TAPER_LENGTH_CELLS) break;
    const t = cumulative / TAIL_TAPER_LENGTH_CELLS;
    scales[i] = TAIL_MIN_RADIUS_SCALE + (1 - TAIL_MIN_RADIUS_SCALE) * t;
  }
  return scales;
}

// ---------------------------------------------------------------------
// Route / trail: the head's own recent committed path, and sampling the
// body from it.
// ---------------------------------------------------------------------

/**
 * One point in the head's committed travel history, oldest-to-newest in
 * a `RoutePoint[]` (so `route[route.length - 1]` is always the CURRENT
 * committed head position). `discontinuous` is true when this point does
 * NOT connect to the one immediately before it via an ordinary one-cell
 * grid edge - a portal or WORMHOLE teleport (or the very first point
 * ever recorded, where there IS no "before"; the flag is meaningless
 * there since nothing walks past index `0` anyway). Never a physics/
 * simulation type - plain grid coordinates plus one boolean, exactly
 * mirroring what `LevelGame.tick()` already committed.
 */
export interface RoutePoint extends Point {
  readonly discontinuous: boolean;
}

/**
 * How much EXTRA route history (in cells, beyond the current logical
 * snake length) is retained - a small safety margin so a growth tick
 * (which can add more than one segment at once - see
 * `GameConfig.bites.chaos.jackpot.growth`) never has to wait more than a
 * tick or two for the route to catch back up to full depth. Retained
 * history is always `bodyLength + this margin`, never unbounded - see
 * `appendRoutePoint()`.
 */
export const ROUTE_HISTORY_MARGIN = 4;

/**
 * Builds the starting route from a level's initial snake snapshot
 * (head-first, e.g. `LevelGameSnapshot.snake` right after `reset()`/a
 * fresh run). The starting body is already a straight, fully-connected
 * chain (see `LevelGame`'s `buildInitialSnakeBody()`), so every point
 * converts directly into an ordinary (non-discontinuous) route edge -
 * this gives full sampling depth from the very first rendered frame,
 * with no fallback-to-logical-position period needed at spawn.
 */
export function buildInitialRoute(headFirstSnake: readonly Point[]): RoutePoint[] {
  const route: RoutePoint[] = [];
  for (let i = headFirstSnake.length - 1; i >= 0; i--) {
    const p = headFirstSnake[i];
    route.push({ x: p.x, y: p.y, discontinuous: false });
  }
  return route;
}

/**
 * Appends the newly committed head position to `route`, returning a NEW
 * array (this module stays pure/functional throughout - `GameScene`
 * reassigns its own field to the result). `discontinuous` is computed
 * from actual geometry against the route's own current last point
 * (`isVisuallyContinuous()` - the exact same predicate
 * `computeSnakeVisualRuns()` uses), never from a portal/WORMHOLE event
 * flag - so a stale flag can never mistag an edge, and an ordinary
 * one-cell move is always correctly recognized even without consulting
 * `LevelGame` at all. Trims from the OLDEST end down to at most
 * `maxLength` entries - callers pass `bodyLength + ROUTE_HISTORY_MARGIN`,
 * so retained history stays proportional to the CURRENT snake length,
 * never unbounded across a long Endless run (see `ROUTE_HISTORY_MARGIN`'s
 * own doc).
 */
export function appendRoutePoint(route: readonly RoutePoint[], newHead: Point, maxLength: number): RoutePoint[] {
  const previous = route[route.length - 1];
  const discontinuous = !previous || !isVisuallyContinuous(previous, newHead);
  const next = [...route, { x: newHead.x, y: newHead.y, discontinuous }];
  const limit = Math.max(1, Math.floor(maxLength));
  if (next.length <= limit) return next;
  return next.slice(next.length - limit);
}

function fallbackPoint(fallbackSnake: readonly Point[], index: number): Point {
  const p = fallbackSnake[index] ?? fallbackSnake[fallbackSnake.length - 1];
  return p ? { x: p.x, y: p.y } : { x: 0, y: 0 };
}

/**
 * Produces `count` render-only body points (head-first, one per grid
 * cell of arc-length) by walking BACKWARD along `route` from the head's
 * own fractional visual position on its most recent edge
 * (`headEdgeProgress`, `0..1` - typically `computeSnakeRenderProgress()`'s
 * output). This is the entire route/trail replacement for the old
 * per-index `previous[i] -> current[i]` interpolation: every sample is
 * derived from the SAME single continuous head path, so a turn's corner
 * is a fixed point in that history that shifts smoothly backward through
 * the samples as the head keeps moving - never a simultaneous per-index
 * morph. `fallbackSnake` is the CURRENT logical snake (head-first,
 * exactly `count` long) - used verbatim (no smoothing) for any sample
 * that can't be reached by walking the retained route, so this never
 * throws, never produces `NaN`, and always returns exactly `count`
 * points even with an unusually short/absent route.
 *
 * A sample falls back in exactly two cases, both by design:
 *  - the retained route simply doesn't go back far enough yet (e.g. the
 *    very first few ticks after a portal/WORMHOLE teleport, before new
 *    history has had time to accumulate past the break - see
 *    `appendRoutePoint()`);
 *  - walking backward would cross a `discontinuous` route point - the
 *    walk always stops AT that boundary rather than treating the
 *    teleport as one more cell of ordinary arc-length, so this can never
 *    sample (or draw, once `computeSnakeVisualRuns()` runs on the
 *    output) a stroke across the board.
 *
 * If the LATEST route edge itself is discontinuous (the head just
 * teleported this very tick), the head snaps exactly to that point -
 * `headEdgeProgress` is ignored entirely, matching the original PR32
 * "a teleported head snaps, never interpolates" rule - and every sample
 * behind it falls back immediately, since there is no ordinary edge yet
 * to walk.
 */
export function sampleSnakeRoute(route: readonly RoutePoint[], headEdgeProgress: number, fallbackSnake: readonly Point[], count: number): Point[] {
  const result: Point[] = [];
  if (count <= 0) return result;
  const n = route.length;
  if (n === 0) {
    for (let i = 0; i < count; i++) result.push(fallbackPoint(fallbackSnake, i));
    return result;
  }

  const head = route[n - 1];
  const hasOrdinaryEdge = n >= 2 && !head.discontinuous;
  const t = hasOrdinaryEdge ? Math.max(0, Math.min(1, headEdgeProgress)) : 0;
  const headPos: Point = hasOrdinaryEdge ? lerpPoint(route[n - 2], head, t) : { x: head.x, y: head.y };

  // The nearest discontinuity walking backward from route[n-2] (the
  // point just behind the head) - any route index at or before it is on
  // the OTHER side of a break and unreachable from the head's current
  // position. Computed once (O(route length)), not per sample, so the
  // whole function stays O(route length + count), never quadratic.
  let reachableFloor = 0;
  if (hasOrdinaryEdge) {
    for (let i = n - 2; i >= 1; i--) {
      if (route[i].discontinuous) {
        reachableFloor = i;
        break;
      }
    }
  }

  for (let d = 0; d < count; d++) {
    if (d === 0) {
      result.push(headPos);
      continue;
    }
    if (!hasOrdinaryEdge) {
      result.push(fallbackPoint(fallbackSnake, d));
      continue;
    }
    const u = d - t;
    if (u <= 0) {
      // Between the head and route[n-2] - the one edge whose length
      // isn't exactly 1 (the head is only partway along it).
      const frac = t <= 0 ? 1 : Math.min(1, d / t);
      result.push(lerpPoint(headPos, route[n - 2], frac));
      continue;
    }
    const k = Math.floor(u);
    const frac = u - k;
    const idxNewer = n - 2 - k;
    const idxOlder = idxNewer - 1;
    if (idxOlder < reachableFloor) {
      result.push(fallbackPoint(fallbackSnake, d));
      continue;
    }
    result.push(lerpPoint(route[idxNewer], route[idxOlder], frac));
  }
  return result;
}
